import { useState, useEffect } from "react";
import { X, Scissors, Tag, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const NeighborhoodCoupon = ({ cms, onClaim }) => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    const wasDismissed = sessionStorage.getItem("coupon_dismissed");
    if (wasDismissed) { setDismissed(true); return; }
    const timer = setTimeout(() => setVisible(true), 2500);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    sessionStorage.setItem("coupon_dismissed", "1");
  };

  const handleMinimize = () => setMinimized(true);
  const handleExpand = () => setMinimized(false);

  if (dismissed) return null;

  // Minimized floating tag
  if (minimized) {
    return (
      <button
        onClick={handleExpand}
        className="fixed bottom-6 right-6 z-50 bg-[#39FF14] text-black px-4 py-3 rounded-full shadow-lg shadow-[#39FF14]/30 flex items-center gap-2 font-bold text-sm hover:scale-105 transition-transform animate-bounce"
        style={{ animationDuration: "2s", animationIterationCount: 3 }}
        data-testid="coupon-minimized"
      >
        <Tag className="w-4 h-4" />
        {cms("neighborhood_discount", "discount_text", "Up to 15% OFF")}
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 transition-all duration-500 ${
        visible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
      }`}
      data-testid="neighborhood-coupon"
    >
      {/* Coupon card */}
      <div className="relative w-[340px] bg-[#0A0A0A] border-2 border-dashed border-[#39FF14] rounded-2xl overflow-hidden shadow-2xl shadow-[#39FF14]/20">
        {/* Top perforated edge */}
        <div className="absolute top-0 left-0 right-0 flex justify-between px-2 -translate-y-[3px]">
          {[...Array(14)].map((_, i) => (
            <div key={i} className="w-4 h-2 bg-[#0A0A0A] rounded-b-full border-b-2 border-x-2 border-dashed border-[#39FF14]" />
          ))}
        </div>

        {/* Close & minimize buttons */}
        <div className="absolute top-3 right-3 flex gap-1 z-10">
          <button onClick={handleMinimize} className="text-[#A1A1AA] hover:text-white transition-colors p-1" title="Minimize">
            <ChevronRight className="w-4 h-4 rotate-90" />
          </button>
          <button onClick={handleDismiss} className="text-[#A1A1AA] hover:text-red-400 transition-colors p-1" data-testid="coupon-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Badge */}
        <div className="pt-5 px-5">
          <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#39FF14]/20 text-[#39FF14] border border-[#39FF14]/40">
            {cms("neighborhood_discount", "badge", "SPECIAL OFFER")}
          </span>
        </div>

        {/* Content */}
        <div className="px-5 pb-2 pt-3">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#39FF14]/10 border border-[#39FF14]/30 flex items-center justify-center shrink-0">
              <Scissors className="w-6 h-6 text-[#39FF14]" />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg leading-tight">
                {cms("neighborhood_discount", "title", "Refer a Neighbor Discount")}
              </h3>
              <p className="text-[#A1A1AA] text-xs mt-0.5">
                {cms("neighborhood_discount", "subtitle", "Save when your neighbors book too!")}
              </p>
            </div>
          </div>
        </div>

        {/* Divider with scissors cut */}
        <div className="relative px-5 py-2">
          <div className="border-t border-dashed border-[#39FF14]/40"></div>
          <div className="absolute left-3 top-1/2 -translate-y-1/2 bg-[#0A0A0A] px-1">
            <Scissors className="w-3 h-3 text-[#39FF14]/50 rotate-180" />
          </div>
        </div>

        {/* Discount highlight */}
        <div className="px-5 pb-3">
          <div className="bg-[#39FF14]/5 border border-[#39FF14]/20 rounded-xl p-3 text-center">
            <p className="text-[#39FF14] text-2xl font-black tracking-tight">
              {cms("neighborhood_discount", "discount_text", "Up to 15% OFF")}
            </p>
            <p className="text-[#A1A1AA] text-xs mt-1 leading-relaxed">
              {cms("neighborhood_discount", "description", "Refer a neighbor and you both receive a discount! When we service multiple homes in your area, everyone saves.")}
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="px-5 pb-3">
          <Button
            onClick={() => {
              localStorage.setItem("claimed_neighborhood_discount", "1");
              onClaim?.();
              handleDismiss();
            }}
            className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold rounded-full"
            data-testid="coupon-claim-btn"
          >
            {cms("neighborhood_discount", "cta_button", "Claim Discount")}
            <ChevronRight className="ml-2 w-4 h-4" />
          </Button>
        </div>

        {/* Fine print */}
        <div className="px-5 pb-4">
          <p className="text-[#52525B] text-[10px] text-center">
            {cms("neighborhood_discount", "fine_print", "Discount applies when referred neighbor books a service")}
          </p>
        </div>

        {/* Bottom perforated edge */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-between px-2 translate-y-[3px]">
          {[...Array(14)].map((_, i) => (
            <div key={i} className="w-4 h-2 bg-[#0A0A0A] rounded-t-full border-t-2 border-x-2 border-dashed border-[#39FF14]" />
          ))}
        </div>
      </div>
    </div>
  );
};

export default NeighborhoodCoupon;
